/**
 * Générateur — Automatisme : faire vivre la notion de nombre quotient en
 * complétant des multiplications à trou (3 × … = 7 puis 3 × 7/3 = …).
 * Attendu ID : 1049 | Niveau : 5ème | Domaine : Nombres rationnels
 * Rubrique : AUTOMATISME n°11 (programme 2025). Définition de 6e : le
 * quotient de a par b est le nombre qui, multiplié par b, donne a, et la
 * fraction a/b EST ce nombre. Deux gestes, RIEN À CALCULER :
 *   - geste 1 : compléter 3 × … = 7 par 7/3, écrit tel quel (accepter la
 *     fraction comme réponse-nombre, ne pas l'inverser en 3/7) ;
 *   - geste 2 : reconnaître 3 × 7/3 = 7 sans multiplier.
 * Labels (cartes exerciseur) — deux cartes (cadrage prof 2026-09-07) :
 *   - Série flash                        (4 lignes saisie, les deux gestes)
 *   - Le nombre qui, multiplié par…      (QCM, Vrai/Faux, Drag croisé)
 *
 * ── Domaine (tout en entiers, valeur = fraction b/a) ─────────────────────
 * Facteur connu a de 2 à 9, résultat b de 1 à 20, a ≠ b. NON MULTIPLE (b
 * n'est pas multiple de a) partout, sauf au QCM où un multiple (3 × … = 12
 * → 12/3, qui vaut aussi 4) et un cas décimal (2 × … = 9 → 9/2) restent
 * minoritaires pour montrer que 12/3 et 9/2 sont des nombres comme les
 * autres. Jamais de multiple en saisie deux cases (4 serait aussi juste).
 * COUPLE SACRIFIÉ : (4, 13), l'exemple du coup de pouce (schéma en barres :
 * une barre 13, dessous 4 parts égales étiquetées 13/4).
 *
 * Saisie deux cases (gabarit de gen_622, étais \rule pour garder la barre
 * visible) : la correction des trous est POSITIONNELLE et exacte (moteur de
 * scoring, zone protégée) : 14/6 serait compté faux. La consigne rappelle
 * la définition en lettres (a/b est le nombre qui, multiplié par b, donne
 * a) : l'attendu « a/b avec les nombres donnés » est alors le seul juste ;
 * la tolérance aux fractions égales est une décision de scoring à part,
 * notée en attente.
 *
 * DISTRACTEURS, familles nommées, chacun vérifié ≠ clé EN VALEUR (rationnel
 * exact), sous quelque écriture que ce soit ; collision → nouveau tirage :
 *   geste 1 : inversion (3/7, TOUJOURS présente) ; soustraction (b − a) ;
 *     refus (« Aucun nombre ne convient ») ; approximation décimale (2,3),
 *     seulement si b/a n'est pas décimal. Le refus n'est pas systématique
 *     (une fois sur deux parmi les autres), sinon il se repère à sa forme.
 *   geste 2 : rien_fait (8/5) ; produit_haut_bas (40/25) ; addition (13/5).
 *   drag croisé : produit (21), somme (10), difference (4) pour 7/3.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, filtrerDistracteurs, tex,
} from '../../../utils';

const ATTENDU_ID = 1049;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_SENS = 'Le nombre qui, multiplié par…';

// ─── Le domaine ───────────────────────────────────────────────────────────

/** Le couple de l'exemple du coup de pouce, sacrifié : 4 × … = 13 (barre 13 en 4 parts). */
export const COUPLES_SACRIFIES = [{ a: 4, b: 13 }];
const estSacrifie = (a, b) => COUPLES_SACRIFIES.some((s) => s.a === a && s.b === b);

const A_MIN = 2;
const A_MAX = 9;
const B_MAX = 20;

/** Un couple (a, b) : a × … = b, trou = b/a. `multiple` autorise b = k·a. */
const verifierCouple = (a, b, multiple = false) => {
    if (!Number.isInteger(a) || !Number.isInteger(b) || a < A_MIN || a > A_MAX || b < 1 || b > B_MAX || a === b) {
        throw new Error(`gen_1049 : couple hors domaine ${a} x ... = ${b}`);
    }
    if (!multiple && b % a === 0) throw new Error(`gen_1049 : multiple non autorisé ${a} x ... = ${b}`);
    if (estSacrifie(a, b)) throw new Error('gen_1049 : couple sacrifié tiré');
    return { a, b };
};

const tirerCouple = (multiple = false) => {
    for (let essai = 0; essai < 80; essai++) {
        const a = randInt(A_MIN, A_MAX);
        const b = randInt(1, B_MAX);
        if (a === b || (!multiple && b % a === 0) || estSacrifie(a, b)) continue;
        return verifierCouple(a, b, multiple);
    }
    return verifierCouple(3, 7);
};

/** Un multiple strict (b = k·a, k ≥ 2), pour le QCM seulement. */
const tirerMultiple = () => {
    for (let essai = 0; essai < 80; essai++) {
        const a = randInt(A_MIN, A_MAX);
        const b = a * randInt(2, Math.floor(B_MAX / a));
        if (b > B_MAX || b === a || estSacrifie(a, b)) continue;
        return verifierCouple(a, b, true);
    }
    return verifierCouple(3, 12, true);
};

const cle = (c) => `${c.a}|${c.b}`;

/** Égalité rationnelle p/q = r/s en croix (q, s > 0). */
const memeValeur = (p, q, r, s) => p * s === r * q;

/** b/a est-il décimal ? (dénominateur réduit sans facteur autre que 2 et 5) */
const estDecimal = (a, b) => (b * 1000) % a === 0;

// ─── Écritures ────────────────────────────────────────────────────────────

/** Fraction à deux cases (gabarit 622) : numérateur b, dénominateur a. */
const fracDeuxCases = (c) => `\\dfrac{{{${c.b}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${c.a}}}}`;
const fracTex = (n, d) => `\\dfrac{${n}}{${d}}`;
const dec1 = (x) => String(x).replace('.', ',');

// ─── Distracteurs ─────────────────────────────────────────────────────────

const REFUS = 'Aucun nombre ne convient';

/**
 * Geste 1 : l'inversion toujours, plus UN autre tiré parmi soustraction,
 * refus, approximation (celle-ci seulement si b/a n'est pas décimal).
 * Retour : [{ texte, code, num, den }] ou null si collision avec la clé.
 */
const distracteursGeste1 = (c) => {
    const inversion = { texte: `$${fracTex(c.a, c.b)}$`, code: 'inversion', num: c.a, den: c.b };
    const autres = [];
    if (c.b - c.a > 0) autres.push({ texte: String(c.b - c.a), code: 'soustraction', num: c.b - c.a, den: 1 });
    autres.push({ texte: REFUS, code: 'refus', num: null, den: null });
    if (!estDecimal(c.a, c.b)) {
        const approx = Math.round((c.b / c.a) * 10) / 10;
        autres.push({ texte: dec1(approx), code: 'approximation', num: Math.round(approx * 10), den: 10 });
    }
    const second = randomChoice(autres);
    const choix = [inversion, second];
    if (choix.some((d) => d.num !== null && memeValeur(d.num, d.den, c.b, c.a))) return null;
    return choix;
};

/** Geste 2 : a × b/a, clé b. Deux distracteurs tirés parmi trois. */
const distracteursGeste2 = (c) => {
    const tous = [
        { texte: `$${fracTex(c.b, c.a)}$`, code: 'rien_fait', num: c.b, den: c.a },
        { texte: `$${fracTex(c.a * c.b, c.a * c.a)}$`, code: 'produit_haut_bas', num: c.a * c.b, den: c.a * c.a },
        { texte: `$${fracTex(c.a + c.b, c.a)}$`, code: 'addition', num: c.a + c.b, den: c.a },
    ];
    const choix = shuffle(tous).slice(0, 2);
    if (choix.some((d) => memeValeur(d.num, d.den, c.b, 1))) return null;
    if (memeValeur(choix[0].num, choix[0].den, choix[1].num, choix[1].den)) return null;
    return choix;
};

const EXPLICATIONS = {
    inversion: 'Le nombre cherché s\'écrit résultat sur facteur : 7 sur 3, pas 3 sur 7.',
    soustraction: 'Le trou n\'est pas ce qui manque pour aller de 3 à 7 : c\'est ce par quoi on multiplie 3.',
    refus: 'Il y a toujours un nombre : c\'est la fraction 7 sur 3, elle n\'a pas besoin de tomber juste.',
    approximation: '2,3 fois 3, ça ne fait pas 7 : la fraction 7 sur 3 est exacte, pas approchée.',
    rien_fait: '3 fois le nombre qui, multiplié par 3, donne 7 : ça donne 7, il n\'y a rien à multiplier.',
    produit_haut_bas: 'On ne multiplie pas le dénominateur : 3 fois 7 sur 3, c\'est 7.',
    addition: 'On multiplie, on n\'additionne pas.',
    produit: 'Le nombre 7 sur 3 ne se multiplie pas par 21.',
    somme: '7 sur 3 n\'est pas une somme.',
    difference: '7 sur 3 n\'est pas une différence.',
    regle: 'Une fraction est le nombre qui, multiplié par son dénominateur, redonne son numérateur.',
};

// ─── Carte 1 : Série flash (4 lignes saisie, 4 max : cadrage prof) ────────
// Trois formes gardées : deux lignes geste 1 (le réflexe le plus dur reste
// majoritaire), une ligne geste 2, une ligne en mots.

const ligneGeste1 = (c) => (randomChoice([true, false])
    ? `$${c.a} \\times ${fracDeuxCases(c)} = ${c.b}$`
    : `$${fracDeuxCases(c)} \\times ${c.a} = ${c.b}$`);
const ligneGeste2 = (c) => (randomChoice([true, false])
    ? `$${c.a} \\times ${fracTex(c.b, c.a)} = {{${c.b}}}$`
    : `$${fracTex(c.b, c.a)} \\times ${c.a} = {{${c.b}}}$`);
const ligneMots = (c) => `$\\text{Quel nombre, multiplié par } ${c.a}\\text{, donne } ${c.b}\\text{ ? } ${fracDeuxCases(c)}$`;

// Rappel en lettres (définition de 6e) plutôt que « avec les nombres de
// l'égalité » (cadrage prof 2026-09-07) : l'élève doit transposer la
// propriété (repérer qui est a, qui est b), pas deviner un sens à pile ou
// face ; et 40/6 n'est pas « a/b avec les nombres donnés », l'attendu 20/3
// est ainsi le seul juste, en accord avec la correction positionnelle.
// Lettres seules par tex() : la regex du rendu en ligne exige une commande
// LaTeX juste après le « $ », sinon « $b$ » s'affiche brut (vécu à l'écran).
const CONSIGNE_FLASH = `<p><b>Complète chaque égalité.</b> Rappel : ${tex('\\dfrac{a}{b}')} est le nombre qui, multiplié par ${tex('b')}, donne ${tex('a')}.</p>`;

const genFlash = () => {
    for (let essai = 0; essai < 80; essai++) {
        const couples = [0, 1, 2, 3].map(() => tirerCouple());
        if (new Set(couples.map(cle)).size < 4) continue;
        const formes = shuffle(['geste1', 'geste1', 'geste2', 'mots']);
        const items = couples.map((c, i) => ({ ...c, forme: formes[i] }));
        const phrase = items.map((it) => {
            if (it.forme === 'geste1') return ligneGeste1(it);
            if (it.forme === 'geste2') return ligneGeste2(it);
            return ligneMots(it);
        }).join('\n');
        const payload = makeTexteTrou(phrase, CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
        payload._meta.params = { items };
        return payload;
    }
    const items = [
        { ...verifierCouple(3, 7), forme: 'geste1' }, { ...verifierCouple(5, 8), forme: 'geste1' },
        { ...verifierCouple(6, 11), forme: 'geste2' }, { ...verifierCouple(7, 2), forme: 'mots' },
    ];
    const phrase = items.map((it) => (it.forme === 'geste1' ? ligneGeste1(it) : it.forme === 'geste2' ? ligneGeste2(it) : ligneMots(it))).join('\n');
    const payload = makeTexteTrou(phrase, CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items };
    return payload;
};

// ─── Carte 2, format A : QCM, les deux gestes ─────────────────────────────

const genQcm = () => {
    for (let essai = 0; essai < 80; essai++) {
        const geste = randomChoice(['geste1', 'geste1', 'geste2']);
        // Les cas multiple et décimal, minoritaires, vivent ici (geste 1).
        const cas = geste === 'geste1' ? randomChoice(['normal', 'normal', 'normal', 'multiple', 'decimal']) : 'normal';
        let c;
        if (cas === 'multiple') c = tirerMultiple();
        else if (cas === 'decimal') {
            c = null;
            for (let k = 0; k < 40 && !c; k++) { const t = tirerCouple(); if (estDecimal(t.a, t.b)) c = t; }
            if (!c) continue;
        } else c = tirerCouple();
        let bon;
        let faux;
        let texte;
        if (geste === 'geste1') {
            faux = distracteursGeste1(c);
            if (!faux) continue;
            bon = `$${fracTex(c.b, c.a)}$`;
            const egalite = randomChoice([true, false]) ? `${c.a} \\times \\ldots = ${c.b}` : `\\ldots \\times ${c.a} = ${c.b}`;
            texte = `<p><b>Pour compléter ${tex(egalite)}, quel nombre écris-tu ?</b></p>`;
        } else {
            faux = distracteursGeste2(c);
            if (!faux) continue;
            bon = String(c.b);
            texte = `<p><b>Combien vaut ${tex(`${c.a} \\times ${fracTex(c.b, c.a)}`)} ?</b></p>`;
        }
        const choix = [bon, ...faux.map((d) => d.texte)];
        if (new Set(choix).size !== 3) continue;
        const payload = makeQcm(choix, bon, texte, opts(L_SENS), false);
        payload.contenu = { ...payload.contenu, sousType: geste, explications: EXPLICATIONS };
        payload._meta.params = { ...c, geste, cas, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    const c = verifierCouple(3, 7);
    const payload = makeQcm([`$${fracTex(7, 3)}$`, `$${fracTex(3, 7)}$`, REFUS], `$${fracTex(7, 3)}$`,
        `<p><b>Pour compléter ${tex('3 \\times \\ldots = 7')}, quel nombre écris-tu ?</b></p>`, opts(L_SENS), false);
    payload.contenu = { ...payload.contenu, sousType: 'geste1', explications: EXPLICATIONS };
    payload._meta.params = { ...c, geste: 'geste1', cas: 'normal', distracteurs: [{ code: 'inversion' }, { code: 'refus' }] };
    return payload;
};

// ─── Carte 2, format B : Vrai ou faux (3 affirmations) ────────────────────

/** Trois types d'affirmation ; la fausse porte l'inversion une fois sur deux. */
const affirmation = (c, type, vraie) => {
    if (type === 'produit') {
        // 4 × 5/4 = 5 ; fausse : = 20 (produit) ou = 5/4 (rien fait)
        const d = vraie ? { texte: String(c.b), code: null }
            : randomChoice([{ texte: String(c.a * c.b), code: 'produit_haut_bas' }, { texte: `$${fracTex(c.b, c.a)}$`, code: 'rien_fait' }]);
        return { ligne: `${tex(`${c.a} \\times ${fracTex(c.b, c.a)}`)} = ${d.texte}`, code: d.code };
    }
    if (type === 'definition') {
        // Le nombre qui, multiplié par 4, donne 9, est 9/4 ; fausse : 4/9
        const f = vraie ? fracTex(c.b, c.a) : fracTex(c.a, c.b);
        return { ligne: `Le nombre qui, multiplié par ${c.a}, donne ${c.b}, est ${tex(f)}`, code: vraie ? null : 'inversion' };
    }
    // 6 × … = 13 se complète par 13/6 (vraie) / ne peut pas se compléter (fausse, refus)
    return vraie
        ? { ligne: `${tex(`${c.a} \\times \\ldots = ${c.b}`)} se complète par ${tex(fracTex(c.b, c.a))}`, code: null }
        : { ligne: `${tex(`${c.a} \\times \\ldots = ${c.b}`)} ne peut pas se compléter`, code: 'refus' };
};

const genVf = () => {
    for (let essai = 0; essai < 80; essai++) {
        const couples = [tirerCouple(), tirerCouple(), tirerCouple()];
        if (new Set(couples.map(cle)).size < 3) continue;
        const types = shuffle(['produit', 'definition', 'refus']);
        const nbVraies = randomChoice([1, 2]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(3 - nbVraies).fill(false)]);
        // L'inversion (l'erreur la plus fréquente) porte au moins une fausse
        // une fois sur deux : la ligne « définition » est alors fausse.
        if (randomChoice([true, false])) {
            const iDef = types.indexOf('definition');
            const iFaux = verites.indexOf(false);
            if (iFaux !== iDef) { verites[iDef] = false; verites[iFaux] = true; }
        }
        const codes = [];
        const lignes = couples.map((c, i) => {
            const aff = affirmation(c, types[i], verites[i]);
            if (aff.code) codes.push(aff.code);
            return aff.ligne;
        });
        if (new Set(lignes).size < 3) continue;
        const payload = makeVraiFaux(lignes, verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_SENS));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { couples, types, verites, distracteurs: codes.map((code) => ({ code })) };
        return payload;
    }
    const couples = [verifierCouple(4, 5), verifierCouple(4, 9), verifierCouple(6, 13)];
    const lignes = [
        affirmation(couples[0], 'produit', true).ligne,
        affirmation(couples[1], 'definition', false).ligne,
        affirmation(couples[2], 'refus', false).ligne,
    ];
    const payload = makeVraiFaux(lignes, [0, 1, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_SENS));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { couples, types: ['produit', 'definition', 'refus'], verites: [true, false, false], distracteurs: [{ code: 'inversion' }, { code: 'refus' }] };
    return payload;
};

// ─── Carte 2, format C : complétion croisée (drag, 2 trous) ───────────────

const genCroiseDrag = () => {
    const c = tirerCouple();
    // « 7/3 est le nombre qui, multiplié par {{3}}, donne {{7}}. » Fraction en
    // KaTeX inline (\frac, style texte) dans une phrase en mode classique.
    const phrase = `$\\frac{${c.b}}{${c.a}}$ est le nombre qui, multiplié par {{${c.a}}}, donne {{${c.b}}}.`;
    const pieges = [
        { valeur: c.a * c.b, code: 'produit' },
        { valeur: c.a + c.b, code: 'somme' },
        { valeur: Math.abs(c.b - c.a), code: 'difference' },
    ];
    const distracteurs = filtrerDistracteurs(phrase, pieges.map((p) => String(p.valeur)));
    const payload = makeTexteTrou(
        phrase,
        '<p><b>Complète la phrase avec les bonnes étiquettes.</b></p>',
        { ...opts(L_SENS), mode: 'drag', distracteurs },
    );
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...c, distracteurs: pieges.filter((p) => distracteurs.includes(String(p.valeur))).map(({ code }) => ({ code })) };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'trois choix ; chaque distracteur est vérifié différent de la clé EN VALEUR (rationnel exact : b − a ≠ b/a, approximation ≠ b/a…) ; collision → nouveau tirage complet',
        'facteur 2..9, résultat 1..20, jamais le couple sacrifié (4, 13) ; non multiple partout sauf QCM (multiple et décimal minoritaires)',
        'chaque distracteur porte son code dans _meta.params.distracteurs ; contenu.sousType = le geste',
    ],
    sens_qcm_unique: {
        support: 'geste 1 (deux fois sur trois) : « a × … = b, quel nombre écris-tu ? » ; geste 2 : « combien vaut a × b/a ? »',
        fixes: {
            inversion: 'geste 1, TOUJOURS : a/b au lieu de b/a (l\'erreur la plus fréquente)',
        },
        rotation: {
            soustraction: 'geste 1 : b − a, il lit une addition à trou',
            refus: 'geste 1 : « Aucun nombre ne convient », il refuse la fraction comme nombre ; pas systématique, sinon il se repère à sa forme',
            approximation: 'geste 1 : b/a arrondi au dixième, seulement quand b/a n\'est pas décimal (sinon l\'arrondi serait la clé)',
            rien_fait: 'geste 2 : b/a tel quel',
            produit_haut_bas: 'geste 2 : (a·b)/(a·a), il multiplie aussi le dénominateur',
            addition: 'geste 2 : (a + b)/a, il additionne',
        },
    },
    sens_tableau_vf: 'trois types d\'affirmation (produit, définition, refus), 1 ou 2 vraies ; la fausse par inversion au moins une fois sur deux',
    sens_texte_trou_drag: 'étiquettes a, b, a·b (produit), a + b (somme), |b − a| (différence), filtrées contre les mots attendus',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    sens_qcm_unique: genQcm,
    sens_tableau_vf: genVf,
    sens_texte_trou_drag: genCroiseDrag,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'sens', label: L_SENS, formatsList: [
        { key: 'sens_qcm_unique', label: 'QCM' },
        { key: 'sens_tableau_vf', label: 'Vrai/Faux' },
        { key: 'sens_texte_trou_drag', label: 'Drag' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
